import {
  normalizeBookingCutoff,
  normalizeBookingLimitPeriodForApi,
} from "./scheduling-limit-period";

/** Build a distinct name for duplicated entities. */
export function duplicateDisplayName(name) {
  const base = (name ?? "").trim() || "Untitled";
  return base.endsWith(" (copy)") ? `${base} 2` : `${base} (copy)`;
}

function normalizeStartTime(t) {
  if (!t || typeof t !== "string") return "09:00";
  const m = t.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "09:00";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/**
 * POST body for /actions/scheduling/classes — mirrors CreateClassModal payload shape.
 */
export function buildDuplicateClassPayload(c) {
  const r = c && typeof c === "object" ? c : {};
  const isRestricted = Boolean(r.is_restricted);
  const aut = Array.isArray(r.allowed_user_types)
    ? [...r.allowed_user_types]
    : [];
  const amp = Array.isArray(r.allowed_membership_plans)
    ? [...r.allowed_membership_plans]
    : [];
  const permPlanIds = Array.isArray(r.permissions?.allowed_plan_ids)
    ? [...r.permissions.allowed_plan_ids]
    : amp;

  const perTp = Boolean(r.per_time_period);
  const perMem = Boolean(r.per_member);

  const coachIds = Array.isArray(r.coach_ids) ? [...r.coach_ids] : [];

  return {
    name: duplicateDisplayName(r.name ?? ""),
    description: typeof r.description === "string" ? r.description : "",
    thumbnail_url: typeof r.thumbnail_url === "string" ? r.thumbnail_url : "",
    duration_minutes: Number(r.duration_minutes) || 60,
    capacity: Number(r.capacity) || 0,
    recurrence_type: r.recurrence_type ?? "weekly",
    recurrence_start_date:
      typeof r.recurrence_start_date === "string"
        ? r.recurrence_start_date.slice(0, 10)
        : "",
    recurrence_end_date:
      typeof r.recurrence_end_date === "string"
        ? r.recurrence_end_date.slice(0, 10)
        : "",
    recurrence_weekdays: Array.isArray(r.recurrence_weekdays)
      ? [...r.recurrence_weekdays]
      : null,
    start_time: normalizeStartTime(r.start_time),
    location: typeof r.location === "string" ? r.location : "",
    timezone: typeof r.timezone === "string" ? r.timezone : "America/New_York",
    ...(coachIds.length > 0 ? { coach_ids: coachIds } : {}),

    is_restricted: isRestricted,
    allowed_user_types: isRestricted ? aut : [],
    allowed_membership_plans: isRestricted ? amp : [],

    per_time_period: perTp,
    per_member: perMem,
    booking_limit_time_period: normalizeBookingLimitPeriodForApi(
      r.booking_limit_time_period ?? "weekly"
    ),
    per_member_time_period: normalizeBookingLimitPeriodForApi(
      r.per_member_time_period ?? "weekly"
    ),
    max_sessions_per_time_period: Number(r.max_sessions_per_time_period) || 10,
    max_sessions_per_member_per_time_period:
      Number(r.max_sessions_per_member_per_time_period) || 2,
    booking_limit_enabled: perTp || perMem || Boolean(r.booking_limit_enabled),
    booking_limit_count: r.booking_limit_count,
    booking_limit_period: normalizeBookingLimitPeriodForApi(
      r.booking_limit_period ?? "weekly"
    ),
    booking_cutoff: normalizeBookingCutoff(r.booking_cutoff),

    permissions: {
      allowed_user_types: isRestricted ? aut : [],
      allowed_plan_ids: isRestricted ? permPlanIds : [],
    },
  };
}

export function cloneSchedulingWindows(windows) {
  if (!Array.isArray(windows)) return [];
  return windows
    .map((w) => {
      const dow = Number(w.day_of_week ?? w.dayOfWeek);
      return {
        day_of_week: dow,
        start_time: String(w.start_time ?? w.startTime ?? ""),
        end_time: String(w.end_time ?? w.endTime ?? ""),
      };
    })
    .filter(
      (w) =>
        !Number.isNaN(w.day_of_week) &&
        w.day_of_week >= 0 &&
        w.day_of_week <= 6 &&
        w.start_time &&
        w.end_time
    );
}

/**
 * POST body for /actions/scheduling/pt-sessions — mirrors CreatePtSessionModal.
 */
export function buildDuplicatePtSessionPayload(session, coachId, windows) {
  const r = session && typeof session === "object" ? session : {};
  const restricted = Boolean(r.is_restricted);
  const aut = Array.isArray(r.allowed_user_types)
    ? [...r.allowed_user_types]
    : [];
  const amp = Array.isArray(r.allowed_membership_plans)
    ? [...r.allowed_membership_plans]
    : [];

  return {
    name: duplicateDisplayName(r.name ?? r.title ?? ""),
    duration_minutes: Number(r.duration_minutes) || 30,
    capacity: Number(r.capacity) || 1,
    location: typeof r.location === "string" ? r.location : "",
    schedule_type: r.schedule_type ?? "custom",
    coach_id:
      coachId ||
      (r.coach_id != null ? String(r.coach_id) : "") ||
      (r.coach?._id != null ? String(r.coach._id) : ""),
    windows: cloneSchedulingWindows(windows),
    is_restricted: restricted,
    allowed_user_types: restricted ? aut : [],
    allowed_membership_plans: restricted ? amp : [],
    booking_limit_enabled:
      r.booking_limit_enabled !== undefined
        ? Boolean(r.booking_limit_enabled)
        : true,
    booking_limit_count: Number(r.booking_limit_count) || 2,
    booking_limit_period: normalizeBookingLimitPeriodForApi(
      r.booking_limit_period ?? "weekly"
    ),
    booking_cutoff: normalizeBookingCutoff(r.booking_cutoff),
  };
}

/**
 * POST body for /actions/scheduling/facilities — mirrors CreateFacilityModal.
 */
export function buildDuplicateFacilityPayload(facility, windows) {
  const r = facility && typeof facility === "object" ? facility : {};
  const restricted = Boolean(r.is_restricted);
  const aut = Array.isArray(r.allowed_user_types)
    ? [...r.allowed_user_types]
    : [];
  const amp = Array.isArray(r.allowed_membership_plans)
    ? [...r.allowed_membership_plans]
    : [];

  const imgs = Array.isArray(r.image_urls)
    ? r.image_urls
    : Array.isArray(r.images)
      ? r.images
      : [];
  const facilityImage =
    typeof r.facility_image_url === "string"
      ? r.facility_image_url
      : (imgs[0] ?? "");

  let image_urls = imgs.filter((u) => typeof u === "string" && u.trim());
  if (image_urls.length === 0 && facilityImage) {
    image_urls = [facilityImage];
  }

  const tz =
    typeof r.timezone === "string" && r.timezone.trim()
      ? r.timezone.trim()
      : "America/New_York";

  const payload = {
    name: duplicateDisplayName(r.name ?? r.title ?? ""),
    description: typeof r.description === "string" ? r.description : "",
    duration_minutes: Number(r.duration_minutes) || 30,
    capacity: Number(r.capacity) || 10,
    location: typeof r.location === "string" ? r.location : "",
    schedule_type: r.schedule_type ?? "custom",
    timezone: tz,
    thumbnail_url: typeof r.thumbnail_url === "string" ? r.thumbnail_url : "",
    facility_image_url: facilityImage,
    is_restricted: restricted,
    booking_limit_enabled:
      r.booking_limit_enabled !== undefined
        ? Boolean(r.booking_limit_enabled)
        : true,
    booking_limit_count: Number(r.booking_limit_count) || 2,
    booking_limit_period: normalizeBookingLimitPeriodForApi(
      r.booking_limit_period ?? "weekly"
    ),
    booking_cutoff: normalizeBookingCutoff(r.booking_cutoff),
    windows: cloneSchedulingWindows(windows),
    image_urls,
    allowed_user_types: restricted ? aut : [],
    allowed_membership_plans: restricted ? amp : [],
  };
  return payload;
}

/** Load windows for duplicate when the list document omits them (same sources as edit). */
export async function resolvePtSessionWindowsForDuplicate(
  row,
  availabilityById
) {
  const raw = row.raw && typeof row.raw === "object" ? { ...row.raw } : {};
  if (Array.isArray(raw.windows) && raw.windows.length > 0) {
    return cloneSchedulingWindows(raw.windows);
  }
  const cached = availabilityById[row.id]?.raw;
  if (cached && Array.isArray(cached.windows) && cached.windows.length > 0) {
    return cloneSchedulingWindows(cached.windows);
  }
  try {
    const res = await fetch(
      `/actions/scheduling/pt-sessions/${encodeURIComponent(row.id)}/availability-windows`,
      { method: "GET", credentials: "same-origin" }
    );
    const data = await res.json().catch(() => ({}));
    if (res.ok && Array.isArray(data.windows)) {
      return cloneSchedulingWindows(data.windows);
    }
  } catch {
    /* ignore */
  }
  return [];
}

export async function resolveFacilityWindowsForDuplicate(cls) {
  const raw = cls.raw && typeof cls.raw === "object" ? { ...cls.raw } : {};
  if (Array.isArray(raw.windows) && raw.windows.length > 0) {
    return cloneSchedulingWindows(raw.windows);
  }
  try {
    const res = await fetch(
      `/actions/scheduling/facilities/${encodeURIComponent(cls.id)}/availability-windows`,
      { method: "GET", credentials: "same-origin" }
    );
    const data = await res.json().catch(() => ({}));
    if (res.ok && Array.isArray(data.windows)) {
      return cloneSchedulingWindows(data.windows);
    }
  } catch {
    /* ignore */
  }
  return [];
}
